import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import { sendEmailViaBackend } from '../services/emailService';
import './Contact.css';

const INTEREST_OPTIONS = [
  { value: '', label: 'Select an option' },
  { value: 'voip-solutions', label: 'VoIP Solutions' },
  { value: 'call-center', label: 'Call Center Services' },
  { value: 'sip-trunking', label: 'SIP Trunking' },
  { value: 'did-services', label: 'DID Services' },
  { value: 'consultation', label: 'Consultation' },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    interest: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage('');

    const interestLabel = INTEREST_OPTIONS.find(
      (opt) => opt.value === formData.interest
    )?.label;

    if (!interestLabel) {
      setSubmitStatus('error');
      setStatusMessage('Please select what you are interested in.');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await sendEmailViaBackend({
        ...formData,
        interest: interestLabel,
      });

      if (result.success) {
        setSubmitStatus('success');
        setStatusMessage(
          result.message ||
            "Thank you for your message! We'll get back to you soon."
        );
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: '',
          interest: '',
        });
      } else {
        setSubmitStatus('error');
        setStatusMessage(
          result.message ||
            'Sorry, there was an error sending your message. Please try again.'
        );
      }
    } catch {
      setSubmitStatus('error');
      setStatusMessage(
        'Sorry, there was an error sending your message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-container">
      <div className="world-map-bg">
        <svg viewBox="0 0 1000 500" className="world-map">
          <path d="M100,200 Q150,180 200,200 Q250,190 300,200 Q350,180 400,200 Q450,190 500,200 Q550,180 600,200 Q650,190 700,200 Q750,180 800,200 Q850,190 900,200" stroke="#e0e0e0" strokeWidth="1" fill="none"/>
          <path d="M150,250 Q200,230 250,250 Q300,240 350,250 Q400,230 450,250 Q500,240 550,250 Q600,230 650,250 Q700,240 750,250 Q800,230 850,250" stroke="#e0e0e0" strokeWidth="1" fill="none"/>
          <path d="M200,300 Q250,280 300,300 Q350,290 400,300 Q450,280 500,300 Q550,290 600,300 Q650,280 700,300 Q750,290 800,300" stroke="#e0e0e0" strokeWidth="1" fill="none"/>
          <path d="M100,150 Q200,140 300,150 Q400,160 500,150 Q600,140 700,150 Q800,160 900,150" stroke="#e0e0e0" strokeWidth="1" fill="none"/>
          <path d="M100,350 Q200,340 300,350 Q400,360 500,350 Q600,340 700,350 Q800,360 900,350" stroke="#e0e0e0" strokeWidth="1" fill="none"/>
        </svg>
      </div>

      <div className="contact-card">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="contact-card-inner"
        >
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-eyebrow">CONTACT</div>
              <h1 className="contact-title">Get in touch</h1>
              <p className="contact-description">
                Use our contact form for all information requests or contact us directly using the contact information below. All information is treated with complete confidentiality and in accordance with our{' '}
                <span className="privacy-link">data protection statement</span>.
              </p>

              <div className="contact-details">
                <p className="contact-subtitle">Feel free to get in touch with us via email or phone</p>
                <div className="contact-methods">
                  <a href="mailto:info@directroutenetworks.com" className="contact-email">
                    info@directroutenetworks.com
                  </a>
                  <span className="contact-phone">+1 (855) 608-8058</span>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <div className="form-header">
                <h3 className="form-title">Send us a Message</h3>
                <p className="form-subtitle">Fill out the form below and we'll get back to you</p>
              </div>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="Enter your first name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Enter your last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Enter your message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interest">What are you interested in?</label>
                  <div className="dropdown-container">
                    <select
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      className="dropdown-select"
                      required
                    >
                      {INTEREST_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <FaChevronDown className="dropdown-icon" />
                  </div>
                </div>

                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>

                {submitStatus === 'success' && (
                  <div className="success-message">{statusMessage}</div>
                )}

                {submitStatus === 'error' && (
                  <div className="error-message">{statusMessage}</div>
                )}

                <p className="privacy-text">
                  By clicking Submit you agree that Direct Route Networks can process your personal data as required for this information request. You have read the{' '}
                  <span className="privacy-link">data protection statement</span>.
                </p>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
